<template>
  <div>
    <div class="dropZone">
      <form enctype="multipart/form-data" novalidate>
        <div
          class="dropbox"
          @dragover="dragging = true"
          @drop="dragging = false"
          @dragleave="dragging = false"
          :class="{ 'dropzone-area': dragging, '': !dragging }"
        >
          <input
            type="file"
            ref="fileUpload"
            :name="uploadFieldName"
            :disabled="isSaving || disabled"
            @change="filesChange($event.target.name, $event.target.files)"
            :accept="acceptedFileTypes"
            class="input-file"
          />
          <p class="h4">
            <span v-if="isInitial">
              Drag your file here to begin or click to browse</span
            >
            <span v-if="isSaving"> Uploading file...</span>
            <span v-if="isFailed"> Error!</span>
          </p>
        </div>
      </form>
    </div>

    <b-modal title="Upload warnings" :id="`warn-modal_${id}`">
      <p>
        File data validation has completed with errors. Please check
        <b-link href="#" @click="loadErrorsLog()">the summary</b-link>
        <br />
        Are you sure you want to upload it?
      </p>

      <template #modal-footer>
        <div class="w-100">
          <b-button
            variant="primary"
            size="sm"
            class="float-right"
            @click="sendFile(true)"
          >
            Upload
          </b-button>
          <b-button
            variant="outline"
            size="sm"
            class="float-right mr-2"
            @click="hideModal"
          >
            Cancel
          </b-button>
        </div>
      </template>

      <b-overlay
        :show="currentStatus === STATUS_SAVING"
        spinner-variant="primary"
        no-wrap
      >
      </b-overlay>
    </b-modal>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";
import NotificationService from "@/services/NotificationService";
import { HttpHandler } from "@/http/HttpHandler";
import store from "@/store/index";

@Component
export default class AppUploadFile extends Vue {
  @Prop({ type: String, required: true })
  id!: string;

  @Prop({ type: String, required: true })
  uploadUrl!: string;

  @Prop({ type: Boolean, required: false, default: false })
  disabled!: boolean;

  @Prop({ type: Boolean, required: false, default: false })
  useForce!: boolean;

  notificationService: NotificationService = new NotificationService();

  STATUS_INITIAL = 0;
  STATUS_SAVING = 1;
  STATUS_SUCCESS = 2;
  STATUS_FAILED = 3;

  uploadedFiles: { name: string; file: any }[] = [];
  currentStatus: number = 0;
  uploadFieldName: string = "file";

  uploadErrors: [] = [];

  dragging: boolean = false;

  get isInitial() {
    return this.currentStatus == this.STATUS_INITIAL;
  }

  get isSaving() {
    return this.currentStatus == this.STATUS_SAVING;
  }

  get isSuccess() {
    return this.currentStatus == this.STATUS_SUCCESS;
  }

  get isFailed() {
    return this.currentStatus == this.STATUS_FAILED;
  }

  filesChange(fieldName: string, fileList: []) {
    this.uploadedFiles = [];
    if (!fileList.length) return;

    Array.from(Array(fileList.length).keys()).map((x: any) => {
      const fileInfo: any = fileList[x];
      this.uploadedFiles.push({ name: fileInfo.name, file: fileInfo });
    });

    this.sendFile();
  }

  reset() {
    this.currentStatus = this.STATUS_INITIAL;
    this.uploadedFiles = [];
    if (this.$refs.fileUpload) {
      //@ts-ignore;
      this.$refs.fileUpload.value = null;
    }
  }

  get acceptedFileTypes(): string[] {
    return [".xlsx"];
  }

  sendFile(force: boolean | null = null) {
    this.currentStatus = this.STATUS_SAVING;

    let formData = new FormData();
    this.uploadedFiles.map(fileInfo => {
      formData.append(this.uploadFieldName, fileInfo.file, fileInfo.name);
    });

    this.uploadErrors = [];
    const forceParam = this.useForce ? force || false : null;
    
    new HttpHandler(store)
      .importData(this.uploadUrl, formData, forceParam)
      .then((data: any) => {
        this.hideModal();
        this.currentStatus = this.STATUS_SUCCESS;
        this.notificationService.showSuccess("The file was uploaded");
        
        // Emit success event to parent
        this.$emit('upload-success', data?.message || "The file was uploaded successfully");
        
        setTimeout(() => {
          this.reset();
        }, 1000);
      })
      .catch((e: any) => {
        console.error('Upload error details:', {
          fullError: e,
          response: e.response,
          data: e.response?.data,
          message: e.message
        });
        
        this.currentStatus = this.STATUS_FAILED;
        
        if (e.response?.data?.ExceptionType === "InvalidExcelFileException") {
          this.uploadErrors = e.response.data.AdditionalData;
          this.showModal();
          
          this.$emit('upload-error', {
            type: 'validation',
            message: 'File validation failed. Please check the errors and try again.',
            errors: e.response.data.AdditionalData,
            error: e
          });
        } else {
          // Extract error message from response
          let errorMessage = "Failed to upload file. Please try again.";
          
          if (e.response?.data?.Message) {
            errorMessage = e.response.data.Message;
          } else if (e.response?.data?.message) {
            errorMessage = e.response.data.message;
          } else if (e.message) {
            errorMessage = e.message;
          }
          
          console.log('Emitting error with message:', errorMessage);
          
          this.notificationService.showError(errorMessage);
          
          // Emit the error
          this.$emit('upload-error', {
            type: 'upload',
            message: errorMessage,
            fullError: e,
            response: e.response?.data
          });
          
          this.reset();
        }
      });
  }

  showModal() {
    this.$bvModal.show(`warn-modal_${this.id}`);
  }

  hideModal() {
    this.reset();
    this.$bvModal.hide(`warn-modal_${this.id}`);
  }

  loadErrorsLog() {
    const messages = this.uploadErrors.map(
      (error: any) =>
        `${error.ExpertLogin} - ${error.Message} [${error.Column}:${error.Row}] \n`
    );
    let file = new Blob(messages, {
      type: "text/plain;charset=utf-8"
    });
    const link = document.createElement("a");
    const filename = "Upload Errors Log.txt";
    const _win = window.navigator as any;
    if (_win && _win.msSaveOrOpenBlob) {
      _win.msSaveOrOpenBlob(file, filename);
    } else if (link.download !== undefined) {
      const url = URL.createObjectURL(file);
      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      link.style.cssText = "visibility:hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
}
</script>

<style lang="scss">
.dropZone {
  padding: 10px;
  border-radius: 1rem;
  background: #dee2e657;
}

.dropbox {
  outline: 2px dashed #bdbdbd;
  outline-offset: -10px;
  color: dimgray;
  padding: 10px;
  min-height: 50px;
  position: relative;
  cursor: pointer;
  border-radius: 1rem;
}

.dropbox:hover,
.dropzone-area {
  outline: 2px dashed #2F45A5;

  p {
    color: #2F45A5;
  }
}

.dropbox p {
  font-size: 1.2em;
  text-align: center;
  padding: 1.5em 0;
}

.input-file {
  opacity: 0;
  width: 100%;
  height: 100%;
  position: absolute;
  cursor: pointer;
}
</style>